/* tslint:disable:max-line-length */
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { Observable, zip, of, throwError } from 'rxjs';
import { map, groupBy, mergeMap, toArray, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ICategoria, IDocumentoAnual } from '../Entities/documentos';
import {
  IAsegurado,
  IBanco,
  IBeneficiario,
  IBeneficiarioVitalicio,
  IBeneficioCobrado,
  IDatoContratante,
  IDatosAseguradoColectivo,
  IDatosContratanteProducto,
  IDatosContratanteRentista,
  IPolizaPagoPendiente,
  IPolizaPagoRealizado,
  ITipoCuenta,
  IDatoBeneficiario,
  IPostDatoBeneficiario,
  ILLVAL,
  IMandatoResponse,
  IAsegurableResponde,
} from '../Entities/entities';
import { getPureRut, getLocalStorage, getRutDigit } from '../helpers/helper';
import { IDatosEjecutiva, IEndoso } from './../Entities/entities';
import { tipoPrima, key_user_info } from '../helpers/constants';
import { AuthService } from 'src/app/shared/services/auth.service';

function getFullRut(rut: string) {
  return rut.split('-').join('');
}

@Injectable({
  providedIn: 'root',
})
export class PersonService {
  vidaUrl = `${environment.url.api}/`;
  apiBff = `${environment.url.apiBff}/`;

  private headers: HttpHeaders;

  constructor(private http: HttpClient, private auth: AuthService) {
    const token = getLocalStorage('token');
    this.headers = new HttpHeaders({
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-IBM-Client-Id': environment.ClientId,
      'Access-Control-Allow-Origin': '*',
    });
    this.headers = this.headers.append(
      'Authorization',
      'Bearer ' + token.access_token
    );
  }

  /**
   * Obtiene los datos del asegurado colectivo.
   *
   * @param idPoliza - El ID de la póliza del asegurado.
   * @returns Un Observable que emite los datos del asegurado colectivo.
   */
  getDatosAseguradosColectivo(
    idPoliza: string
  ): Observable<IDatosAseguradoColectivo> {
    const userInfo = getLocalStorage(key_user_info);
    const allRut = userInfo.rut.split('-').join('');

    return this.http
      .get<any>(
        `${this.apiBff}BffSitioPrivado/policy/${idPoliza}/insured/${allRut}?name=${userInfo.family_name}`,
        {
          headers: this.headers,
        }
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          console.error('Error al obtener los datos:', err.message);
          return of([]);
        }),
        map(data => {
          if (!data || !data.data || !data.data.people) {
            throw new Error('Data is undefined or null');
          }

          const person = data.data.people;
          let telefono = '';
          let correo = '';

          if (Array.isArray(person.contact)) {
            person.contact.forEach(c => {
              if (c.type === 'PHONE') {
                telefono = c.detail;
              } else if (c.type === 'EMAIL') {
                correo = c.detail;
              }
            });
          }

          return {
            Nombres: person.names,
            Apellidos: person.surNames,
            Rut: person.rut,
            Telefono: telefono,
            Direccion: person.address.address,
            ComunaCodigo: person.address.township.cod,
            Comuna: person.address.township.name,
            Region: person.address.region.name,
            RegionCodigo: person.address.region.cod,
            BancoCodigo: person.bankAccount.bank.code,
            Banco: person.bankAccount.bank.name,
            TipoCuenta: person.bankAccount.accountType.name,
            NumeroCuenta: person.bankAccount.accountNumber,
            Ciudad: person.address.city.name,
            CiudadCodigo: person.address.city.cod,
            Correo: correo,
            FechaNacimiento: person.birthDate,
          } as IDatosAseguradoColectivo;
        })
      );
  }

  public postDatosAseguradosColectivo(
    data: IDatosAseguradoColectivo,
    poliza: string,
    lineaNegocio: string
  ): Observable<any> {
    const userInfo = getLocalStorage(key_user_info);

    const body = {
      Entrada: {
        lineaNegocio,
        poliza,
        rut: getFullRut(userInfo.rut),
        codigoRegion: data.RegionCodigo,
        codigoComuna: data.ComunaCodigo,
        codigoCiudad: data.CiudadCodigo,
        direccion: data.Direccion,
        correo: data.Correo,
        celular: data.Telefono,
        usuario: getPureRut(userInfo.rut),
        codigoBanco: data.BancoCodigo == '0' ? '' : data.BancoCodigo,
        tipoCuenta: data.TipoCuenta,
        numeroCuenta: data.NumeroCuenta ? data.NumeroCuenta : '',
      },
    };

    return this.http.post<any>(
      `${this.vidaUrl}ind/Clientes/Postdatoscliente/ModificarDatosProducto/`,
      body
    );
  }

  public postDatosRentista(data: any): Observable<any> {
    const userInfo = getLocalStorage(key_user_info);
    const RutLimpio = getFullRut(userInfo.rut);

    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };

    const body = {
      poliza: data.poliza,
      rut: userInfo.rut,
      NumeroPersona: data.NumeroPersona,
      correo: data.Correo,
      TelefonoParticular: data.Telefono,
      celular: data.celular,
      direccion: data.Direccion,
      FormaPago: data.FormaPago,
      numeroCuenta: data.NumeroCuenta ? data.NumeroCuenta : '',
      codigoBanco: data.BancoCodigo,
      TipoSeguro: data.TipoSeguro,
      tipoCuenta: data.tiposCuenta,
    };

    return this.http.put<any>(
      `${this.vidaUrl}rv/Clientes/Datos/${data.poliza}/${userInfo.rut}/${data.NumeroPersona}`,
      body,
      httpOptions
    );
  }

  public GetFechaCierreRRVV(): Observable<ILLVAL> {
    const p_TIPOLVAL = 'FECRVIT';
    const p_CODLVAL = 'RVIT';
    return this.http.get<ILLVAL>(
      `${this.vidaUrl}com/Servicio/Parametros/lval/${p_TIPOLVAL}/${p_CODLVAL}`
    );
  }

  postChangePass(data): Observable<any> {
    const userInfo = getLocalStorage(key_user_info);
    const body = {
      rut: getFullRut(userInfo.rut),
      ...data,
    };

    return this.http.post<any>(
      `${this.vidaUrl}ind/Usuarios/Modificarcontrasena/CambiarClave/`,
      body
    );
  }

  getBancos(tipoPoliza: string): Observable<IBanco[]> {
    const userInfo = getLocalStorage(key_user_info);
    return this.http
      .get<any>(
        `${this.vidaUrl}com/Servicios/Svbanco/ListarBancos/${userInfo.rut}/${tipoPoliza}`
      )
      .pipe(
        map(res => {
          return res.DetallesBanco;
        })
      );
  }

  getTiposCuenta(): Observable<ITipoCuenta[]> {
    const userInfo = getLocalStorage(key_user_info);
    return this.http
      .get<any>(
        `${this.vidaUrl}ind/Polizas/Ubicaciones/ListarRegiones/${userInfo.rut}`
      )
      .pipe(
        map(res => {
          return res.TipoCuenta;
        })
      );
  }

  public getDatosContratante(idPoliza: string): Observable<IDatoContratante> {
    const userInfo = getLocalStorage(key_user_info);

    return this.http.get<IDatoContratante>(
      `${this.vidaUrl}ind/Polizas/Contratantes/ObtenerDatosContratante/${idPoliza}/${userInfo.rut}`
    );
  }

  public getDatosContratanteProducto(
    idPoliza: string,
    lineaNegocio: string,
    tipoSeguro?: string
  ): Observable<IDatosContratanteProducto> {
    const userInfo = getLocalStorage(key_user_info);
    let url = '';

    switch (lineaNegocio) {
      case 'INDIVIDUAL':
        url = `${
          this.vidaUrl
        }ind/Clientes/Datoscliente/ObtenerDatosProducto/${idPoliza}/${getPureRut(
          userInfo.rut
        )}/${userInfo.rut}/${lineaNegocio}`;
        break;

      case 'RRVV':
        url = `${
          this.vidaUrl
        }rv/Polizas/Causantes/ObtenerDatosContratante/${idPoliza}/${getPureRut(
          userInfo.rut
        )}/${tipoSeguro}`;
        break;
      default:
        break;
    }

    switch (lineaNegocio) {
      case 'INDIVIDUAL':
        return this.http
          .get<any>(url)
          .pipe(map(response => response.DatosProducto));
        break;

      case 'RRVV':
        return this.http.get<any>(url).pipe(map(response => response));
        break;
      default:
        return;
        break;
    }
  }

  public getDatosContratanteProductoFake(
    idPoliza: string,
    lineaNegocio: string,
    tipoSeguro?: string
  ): Observable<IDatosContratanteProducto> {
    const rut = '14119682-0';
    let url = '';

    switch (lineaNegocio) {
      case 'INDIVIDUAL':
        url = `${
          this.vidaUrl
        }ind/Clientes/Datoscliente/ObtenerDatosProducto/${idPoliza}/${getPureRut(
          rut
        )}/${rut}/${lineaNegocio}`;
        break;

      case 'RRVV':
        url = `${
          this.vidaUrl
        }rv/Polizas/Causantes/ObtenerDatosContratante/${idPoliza}/${getPureRut(
          rut
        )}/${tipoSeguro}`;
        break;
      default:
        break;
    }

    switch (lineaNegocio) {
      case 'INDIVIDUAL':
        return this.http
          .get<any>(url)
          .pipe(map(response => response.DatosProducto));
        break;

      case 'RRVV':
        return this.http.get<any>(url).pipe(map(response => response));
        break;
      default:
        return;
        break;
    }
  }

  public getDatosContratanteRentista(
    idPoliza: string,
    lineaNegocio: string,
    tipoSeguro?: string
  ): Observable<IDatosContratanteRentista> {
    const userInfo = getLocalStorage(key_user_info);
    let url = '';

    switch (lineaNegocio) {
      case 'INDIVIDUAL':
        url = `${
          this.vidaUrl
        }ind/Clientes/Datoscliente/ObtenerDatosProducto/${idPoliza}/${getPureRut(
          userInfo.rut
        )}/${userInfo.rut}/${lineaNegocio}`;
        break;

      case 'RRVV':
        url = `${
          this.vidaUrl
        }rv/Polizas/Causantes/ObtenerDatosContratante/${idPoliza}/${getPureRut(
          userInfo.rut
        )}/${tipoSeguro}`;
        break;
      default:
        break;
    }

    switch (lineaNegocio) {
      case 'INDIVIDUAL':
        return this.http
          .get<any>(url)
          .pipe(map(response => response.DatosProducto));
        break;

      case 'RRVV':
        return this.http.get<any>(url).pipe(map(response => response));
        break;
      default:
        return;
        break;
    }
  }

  public getAsegurados(idPoliza: string): Observable<any> {
    const token = getLocalStorage('token');
    const nombreCompleto = [];

    this.headers = new HttpHeaders({
      dominio: 'SitioPrivado',
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-IBM-Client-Id': environment.ClientId,
      'Access-Control-Allow-Origin': '*',
      Authorization: 'Bearer ' + token.access_token,
    });

    return this.http
      .get<any>(`${this.apiBff}BffSitioPrivado/policy/${idPoliza}/insureds`, {
        headers: this.headers,
      })
      .pipe(
        catchError((err: HttpErrorResponse) => {
          console.error('Error al obtener los datos:', err.message);
          return of([]);
        }),
        mergeMap(response => {
          const insuredList = response.data.insured;

          const aseguradosCobertura = insuredList.flatMap(insured => {
            return insured.policy.map(policy => ({
              Nombres: insured.people.names,
              Apellidos: insured.people.surNames,
              FechaNacimiento: insured.people.birthDate,
              CodigoParentesco: insured.people.relationship.code,
              Parentesco: insured.people.relationship.name,
              Cobertura: policy.coverage.name,
              IDCobertura: Number(policy.coverage.id),
              CodigoCobertura: policy.coverage.code,
              Capital: parseFloat(policy.coverage.capital),
              PrimaBruta: parseFloat(policy.coverage.grossPremium),
              CodigoRamo: policy.branch.code,
              DescripcionRamo: policy.branch.name,
            }));
          });

          return of({ Coberturas: aseguradosCobertura });
        }),
        mergeMap(asegurados => {
          const aseguradosCobertura = asegurados.Coberturas as IAsegurado[];
          return aseguradosCobertura
            .sort((a, b) => (a.DescripcionRamo < b.DescripcionRamo ? -1 : 1))
            .map(a => {
              if (
                !nombreCompleto.find(
                  f => f === `${a.Nombres} ${a.Apellidos} ${a.DescripcionRamo}`
                )
              ) {
                a.NombreCompleto = `${a.Nombres} ${a.Apellidos}`;
                nombreCompleto.push(
                  `${a.Nombres} ${a.Apellidos} ${a.DescripcionRamo}`
                );
              } else {
                a.FechaNacimiento = '';
                a.Parentesco = '';
              }
              return a;
            })
            .sort((a, b) => (a.CodigoParentesco < b.CodigoParentesco ? -1 : 1));
        }),
        groupBy(
          asegurado => asegurado.DescripcionRamo,
          a => a
        ),
        mergeMap(group => zip(of(group.key), group.pipe(toArray())))
      );
  }

  public getBeneficiarios(idPoliza: string): Observable<IBeneficiario[]> {
    const userInfo = getLocalStorage(key_user_info);
    return this.http
      .get<any>(
        `${this.vidaUrl}ind/Polizas/Beneficiarios/ListarBeneficiarios/${idPoliza}/${userInfo.rut}`
      )
      .pipe(
        map(data => {
          if (data) {
            return data.Beneficiario;
          } else {
            return null;
          }
        })
      );
  }

  public getBeneficiariosVitalicio(
    idPoliza: string,
    tipo: string
  ): Observable<IBeneficiarioVitalicio[]> {
    const userInfo = getLocalStorage(key_user_info);
    return this.http
      .get<any>(
        `${
          this.vidaUrl
        }rv/Polizas/Beneficiarios/ListarBeneficiarios/${idPoliza}/${getFullRut(
          userInfo.rut
        )}/${tipo}`
      )
      .pipe(
        map(data => {
          if (data) {
            return data.Beneficiario;
          } else {
            return null;
          }
        })
      );
  }

  public getEjecutiva(idPoliza): Observable<IDatosEjecutiva> {
    const userInfo = getLocalStorage(key_user_info);

    const url = `${
      this.vidaUrl
    }ind/Polizas/Ejecutivas/ObtenerEjecutiva/${idPoliza}/${getPureRut(
      userInfo.rut
    )}`;
    return this.http
      .get<any>(url)
      .pipe(map(response => (response ? response : {})));
  }

  public getBeneficiarioVitalicio(
    idPoliza: string,
    tipo: string
  ): Observable<IBeneficiarioVitalicio> {
    const userInfo = getLocalStorage(key_user_info);

    const url = `${
      this.vidaUrl
    }rv/Polizas/Beneficiarios/ObtenerBeneficiario/${idPoliza}/${
      userInfo.rut
    }/${tipo}/${getFullRut(userInfo.rut)}`;

    return this.http
      .get<any>(url)
      .pipe(map(response => (response ? response.DetalleBeneficiario : {})));
  }

  public postContratanteProducto(
    data: IDatosContratanteProducto,
    poliza: string,
    lineaNegocio: string
  ): Observable<any> {
    const token = getLocalStorage('token');
    this.headers = new HttpHeaders({
      dominio: 'SitioPrivado',
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-IBM-Client-Id': environment.ClientId,
      'Access-Control-Allow-Origin': '*',
      Authorization: 'Bearer ' + token.access_token,
    });

    const userInfo = getLocalStorage(key_user_info);

    let url = '';
    switch (lineaNegocio) {
      case 'INDIVIDUAL':
        url =
          `BffSitioPrivado/ind/CustomerProfile/customer/` +
          getFullRut(userInfo.rut);
        break;
      case 'VITALICIO':
        return;
        break;
      default:
        break;
    }

    const body = {
      Numpol: +poliza,
      Numid: +getPureRut(userInfo.rut),
      TipoId: 'V',
      TelefonoParticular: data.Celular,
      TelefonoComercial: data.TelefonoComercial,
      Email: data.Correo,
      Estado: data.CodigoRegion,
      Ciudad: data.CodigoCiudad,
      Comuna: data.CodigoComuna,
      DireccionParticular: data.Direccion,
      EstadoComercial: data.CodigoRegionComerc,
      CiudadComercial: data.CodigoCiudadComerc,
      ComunaComercial: data.CodigoComunaComerc,
      DireccionComercial: data.DireccionComercial,
      EstadoCorrespondencia: data.CodigoRegionCorres,
      CiudadCorrespondencia: data.CodigoCiudadCorres,
      ComunaCorrespondencia: data.CodigoComunaCorres,
      DireccionCorrespondencia: data.DireccionCorres,
      Mail: data.Correo,
    };
    return this.http.patch<any>(`${this.apiBff}${url}`, body, {
      headers: this.headers,
    });
  }

  public getPolizaPagosRealizados(
    idPoliza: string
  ): Observable<IPolizaPagoRealizado[]> {
    const userInfo = getLocalStorage(key_user_info);
    const numId = getPureRut(userInfo.rut);

    return this.http
      .get<IPolizaPagoRealizado[]>(
        `${this.vidaUrl}ind/Polizas/Pagos/ListarPagosRealizados/${numId}/${idPoliza}/${userInfo.rut}`
      )
      .pipe(
        map(data => {
          if (!data) {
            return null;
          }
          return data.sort((a, b) => {
            return moment(a.FechaPago, 'DD-MM-YYYY').isBefore(
              moment(b.FechaPago, 'DD-MM-YYYY')
            )
              ? 1
              : -1;
          });
        })
      );
  }

  public getPolizaPagosPendientes(
    idPoliza: any
  ): Observable<IPolizaPagoPendiente[]> {
    const userInfo = getLocalStorage(key_user_info);

    const numId = getPureRut(userInfo.rut);
    const dv = getRutDigit(userInfo.rut);

    return this.http
      .get<any>(
        `${this.vidaUrl}ind/Polizas/Pagos/ListarPrimasImpagas/${numId}/${dv}/${idPoliza}/${userInfo.rut}`
      )
      .pipe(
        map(data => {
          return !data
            ? null
            : data
                .map(x => {
                  x.Checked = false;
                  x.TipoPrima = tipoPrima.periodica;
                  return x;
                })
                .sort((a, b) =>
                  moment(a.Periodo, 'MM-YYYY').toDate() <
                  moment(b.Periodo, 'MM-YYYY').toDate()
                    ? 1
                    : -1
                );
        })
      );
  }

  public getBeneficiarosCobrados(
    idPoliza: string
  ): Observable<IBeneficioCobrado[]> {
    const userInfo = getLocalStorage(key_user_info);

    return this.http
      .get<any>(
        `${this.vidaUrl}ind/Polizas/Beneficios/ListarBeneficios/${idPoliza}/${userInfo.rut}`
      )
      .pipe(
        map(data => {
          if (data) {
            return data.Beneficio;
          } else {
            return null;
          }
        })
      );
  }

  public getEndosos(idPoliza: string): Observable<IEndoso[]> {
    const userInfo = getLocalStorage(key_user_info);

    return this.http
      .get<any>(
        `${this.vidaUrl}ind/Polizas/Endosos/ListarEndosos/${idPoliza}/${userInfo.rut}`
      )
      .pipe(
        map(data => {
          if (!data) {
            return null;
          }
          return data.Endoso;
        })
      );
  }

  public postMandarCorreoEjecutiva(
    idTypeDoc: number,
    nombre: string,
    telefono: string,
    message: string,
    poliza: string
  ): Observable<any> {
    const httpOptions = { headers: this.headers };
    const body = {
      ContractorName: nombre,
      Phone: telefono,
      Message: message,
      Poliza: poliza,
    };
    return this.http.post<any>(
      `${environment.url.apiBff}/BffSitioPrivado/SendMail/Type/${idTypeDoc}`,
      body,
      httpOptions
    );
  }

  public postMandarCorreoPDF(
    to: string,
    message: string,
    Doc: object
  ): Observable<any> {
    const body = {
      from: 'vidasecurity@security.cl',
      to,
      cc: '',
      cco: '',
      subject: 'Liquidación de pensión RRVV',
      isHtml: true,
      Code: '',
      GroupCode: '',
      body: message,
      Docs: Doc,
    };

    return this.http.post<any>(`${this.vidaUrl}com/Mail/External/`, body);
  }

  getMisDocumentos(): Observable<ICategoria[]> {
    const userInfo = getLocalStorage(key_user_info);
    const usuario = getFullRut(userInfo.rut);
    const rut = getPureRut(userInfo.rut);
    return this.http
      .get<any>(
        `${this.vidaUrl}com/Servicios/Documentoscliente/ListarDocumentos/${rut}/${usuario}`
      )
      .pipe(
        map(data => {
          if (data) {
            return data.Categoria;
          } else {
            return [];
          }
        })
      );
  }

  getMiDocumentoAnual(idPoloza): Observable<IDocumentoAnual> {
    const userInfo = getLocalStorage(key_user_info);
    const usuario = getFullRut(userInfo.rut);
    return this.http.get<any>(
      `${this.vidaUrl}ind/Polizas/Documentos/ObtenerUltimaCartola/${idPoloza}/${usuario}`
    );
  }
  public postDatosBeneficiarios(data: IPostDatoBeneficiario) {
    const body = {
      ...data,
    };

    return this.http.post<any>(
      `${this.vidaUrl}ind/Polizas/Postbeneficiarios/ModificarDatosBeneficiario/`,
      body
    );
  }

  public getDatosBeneficiarios(
    idPoliza,
    idAseguro,
    nroBeneficiario
  ): Observable<IDatoBeneficiario> {
    const userInfo = getLocalStorage(key_user_info);
    const rut = getFullRut(userInfo.rut);
    return this.http
      .get<any>(
        `${this.vidaUrl}ind/Polizas/Beneficiarios/ObtenerDatosBeneficiario/${idPoliza}/${idAseguro}/${nroBeneficiario}`
      )
      .pipe(map(data => data.Beneficiarios));
  }

  public getEndososByRutBice(rut: string, idPoliza: number): Observable<any> {
    const httpOptions = {
      headers: this.headers,
    };
    const serviceURL = `${
      this.apiBff
    }BffSitioPrivado/GetCaseCrmBice/${rut.replace('-', '')}`;
    return this.http.get<any>(serviceURL, httpOptions);
  }

  GetClientProfile(rut: number, dv: string): Observable<any> {
    const token = getLocalStorage('token_sso');
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token.token}`,
        LR_USUARIO: rut + dv,
        Sistema: 'Clientes',
        LR_CODINTER: '00000000',
        'cache-control': 'no-cache',
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };
    const url =
      environment.url.api +
      '/com/CustomerProfile/client/' +
      rut.toString() +
      dv;
    return this.http.get<any>(url, httpOptions).pipe(
      catchError(error => {
        return throwError(error);
      })
    );
  }

  esAsegurable(
    rut: number,
    dv: string,
    CodProd: string,
    CodPlan: string,
    RevPlan: string,
    CodCober: string,
    nombre: string,
    apellido: string,
    FechaNacimiento: string,
    pais: string
  ): Observable<IAsegurableResponde> {
    // TODO: Llamada al servicio de persona con los parametros.
    const token = getLocalStorage('token_sso');

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token.token}`,
        LR_USUARIO: rut + dv,
        Sistema: 'Clientes',
        LR_CODINTER: '00000000',
        'cache-control': 'no-cache',
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };

    let url =
      environment.url.api +
      '/ind/UnderwritingDecisioning/PersonService/person/:rutdv/insurable?product=:CodProd&codPlan=:CodPlan&revPlan=:RevPlan&codCober=:CodCober&name=:nombre&lastname=:apellido&birthDate=:FechaNacimiento&country=:pais&canal=:canal';
    url = url.replace(':rutdv', rut.toString() + dv);
    url = url.replace(':CodProd', CodProd);
    url = url.replace(':CodPlan', CodPlan);
    url = url.replace(':RevPlan', RevPlan);
    url = url.replace(':CodCober', CodCober);
    url = url.replace(':nombre', nombre);
    url = url.replace(':apellido', apellido);
    url = url.replace(':FechaNacimiento', FechaNacimiento);
    url = url.replace(':pais', pais);
    url = url.replace(':canal', environment.Canal);

    return this.http.get<IAsegurableResponde>(url, httpOptions);
  }

  getMandatoCliente(rut: number, dv: string): Observable<IMandatoResponse> {
    const token = getLocalStorage('token_sso');

    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: `Bearer ${token.token}`,
        LR_USUARIO: rut + dv,
        Sistema: 'Clientes',
        LR_CODINTER: '00000000',
        'cache-control': 'no-cache',
        'X-IBM-Client-Id': environment.ClientId,
      }),
    };
    let url =
      environment.url.api +
      '/ind/Payments/orderPayment/client/:rutdv/period/:periodQuantity/:periodicity';
    url = url.replace(':rutdv', rut + dv);
    url = url.replace(':periodQuantity', '2');
    url = url.replace(':periodicity', 'M');
    return this.http.get<IMandatoResponse>(url, httpOptions);
  }
}
